import React, { useEffect, useCallback, memo } from 'react';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getChanged } from '../../utils/ChangeSpy';
import { findUsers, changeField, clearSuggest, makeContactExecutive } from '../../actions/contact/contactExecutive';
import { reloadContactDetail } from '../../actions/contactDetail';
import { usePopupContext } from '../../contexts/PopupContext';

const onGetValue = suggestion => suggestion?.value ?? '';

const ExecutiveRolePopupContainer = ({
  children,
  contact,
  executive,
  user,
  findUsers,
  changeField,
  makeContactExecutive,
  reloadContactDetail,
}) => {
  const { closePopup } = usePopupContext();

  useEffect(() => {
    const module = {
      username: user.get('module') || '',
      id: user.get('moduleId') || 0,
    };
    const supervisor = {
      username: user.get('userName') || '',
      id: user.get('id') || 0,
    };

    changeField({ name: 'suggestDirector', value: module.username });
    changeField({ name: 'suggestAnalyst', value: supervisor.username });
    changeField({ name: 'recordOwnerId', value: module.id });
    changeField({ name: 'recordSubOwnerId', value: supervisor.id });
  }, [user]);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleCreate = useCallback(() => {
    const id = contact.get('id', 0);
    const body = getChanged(executive);

    makeContactExecutive({ ...body, id }, () => {
      reloadContactDetail({ id }, handleClose);
    });
  }, [contact, executive]);

  const handleFetchUsers = useCallback(
    ({ value }) => {
      findUsers({ filter: value });
    },
    [findUsers],
  );

  const handleUpdateSuggestions = useCallback(
    (suggestionName, param) => {
      if ('text' in param) {
        changeField({ name: suggestionName, value: param.text });
      }

      if ('suggestions' in param) {
        changeField({ name: 'suggests', value: param.suggestions });
      }
    },
    [changeField],
  );

  const handleUpdateDirector = useCallback(param => handleUpdateSuggestions('suggestDirector', param), [
    handleUpdateSuggestions,
  ]);
  const handleUpdateAnalyst = useCallback(param => handleUpdateSuggestions('suggestAnalyst', param), [
    handleUpdateSuggestions,
  ]);

  const handleSelectDirector = useCallback(
    (event, { suggestion }) => {
      changeField({ name: 'suggestDirector', value: suggestion.text });
    },
    [changeField],
  );

  const handleSelectAnalyst = useCallback(
    (event, { suggestion }) => {
      changeField({ name: 'suggestAnalyst', value: suggestion.text });
    },
    [changeField],
  );

  const suggestDirector = {
    onFetch: handleFetchUsers,
    onUpdate: handleUpdateDirector,
    getValue: onGetValue,
    onSelect: handleSelectDirector,
    value: executive.get('suggestDirector', ''),
  };

  const suggestAnalyst = {
    onFetch: handleFetchUsers,
    onUpdate: handleUpdateAnalyst,
    getValue: onGetValue,
    onSelect: handleSelectAnalyst,
    value: executive.get('suggestAnalyst', ''),
  };

  return (
    <>
      <Popups.ExecutiveRolePopup
        contact={contact}
        executive={executive}
        onClose={handleClose}
        onCreate={handleCreate}
        suggestAnalyst={suggestAnalyst}
        suggestDirector={suggestDirector}
      />
      {children}
    </>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    contact: state.contact.info,
    executive: state.contact.executive,
    user: state.auth.get('user', null),
  };
}

export { ExecutiveRolePopupContainer };
export default connect(
  mapStateToProps,
  {
    findUsers,
    clearSuggest,
    makeContactExecutive,
    reloadContactDetail,
    changeField,
  },
  mergeProps,
  connectOptions,
)(memo(ExecutiveRolePopupContainer));
